import { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { inventoryService } from '../services/inventoryService';

export const useSaleReturn = ({ isOpen, initialInvoiceNo, serviceType, dynamicSettings, onSuccess, onClose }) => {
  const [form, setForm] = useState({
    invoiceNo: initialInvoiceNo || '',
    invoiceDate: new Date().toISOString().split('T')[0],
    billNo: `SR-${Math.floor(100000 + Math.random() * 900000)}`,
    reason: '',
    notes: '',
    customer: '',
    store: '',
    transportMode: 'Road',
    vehicleNo: '',
    trackingNo: ''
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [searchText, setSearchText] = useState(initialInvoiceNo || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentSales, setRecentSales] = useState([]);

  // Fetch recent sales for the dropdown
  useMemo(() => {
    if (isOpen) {
      inventoryService.fetchSales({ limit: 10 }).then(res => {
        if (res?.data?.combined) {
          setRecentSales(res.data.combined);
        }
      }).catch(err => console.error(err));
    }
  }, [isOpen]);

  const filteredInvoices = useMemo(() => {
    if (!searchText) return recentSales;
    const lower = searchText.toLowerCase();
    return recentSales.filter(p =>
      p.invoiceNo?.toLowerCase().includes(lower) ||
      (p.customer && p.customer.toLowerCase().includes(lower))
    );
  }, [searchText, recentSales]);

  const handleFetchInvoice = async (invoiceNumber) => {
    if (!invoiceNumber) return;
    setFetching(true);
    try {
      const res = await inventoryService.fetchSales({ search: invoiceNumber });
      const sale = res?.data?.combined?.find(s => s.invoiceNo === invoiceNumber);
      if (sale) {
        handleLoadSale(sale);
        toast.success("Invoice loaded successfully");
      } else {
        toast.error("Sale invoice not found!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch sale");
    } finally {
      setFetching(false);
      setShowDropdown(false);
    }
  };

  const handleLoadSale = (sale) => {
    setForm(prev => ({
      ...prev,
      invoiceNo: sale.invoiceNo,
      invoiceDate: sale.date ? new Date(sale.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      customer: typeof sale.customer === 'object' ? (sale.customer?.name || sale.customer?.title || '') : (sale.customer || 'Unknown Customer'),
      store: sale.store?._id || sale.store || ''
    }));

    const formattedRows = (sale.items || []).map(item => ({
      _id: item._id || Math.random().toString(),
      item: item.item?._id || item.item,
      inventoryId: item.item?._id || item.item,
      name: item.item?.description || item.itemName || 'Unknown Item',
      itemCode: item.item?.articleId || 'N/A',
      brand: item.item?.brand || 'N/A',
      hsnCode: item.item?.hsnCode || 'N/A',
      unit: item.unit || 'Units',
      maxReturn: item.qty || 0,
      returnedQty: 0,
      rate: item.rate || 0,
      taxPercent: item.taxPercent || 0,
      reason: '',
      selected: false
    }));

    setRows(formattedRows);
    setSearchText(sale.invoiceNo);
  };

  const updateRow = (idx, val) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, returnedQty: val } : r));
  };

  const toggleRowSelection = (idx) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, selected: !r.selected } : r));
  };

  const totalRefund = useMemo(() => {
    return rows.filter(r => r.selected).reduce((sum, r) => sum + ((r.returnedQty || 0) * r.rate), 0);
  }, [rows]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedRows = rows.filter(r => r.selected);
    
    if (selectedRows.length === 0) {
      toast.error("Please select at least one item to return");
      return;
    }

    const invalidRow = selectedRows.find(r => !r.returnedQty || r.returnedQty <= 0 || r.returnedQty > r.maxReturn);
    if (invalidRow) {
      toast.error(`Invalid return quantity for ${invalidRow.name}. Available: ${invalidRow.maxReturn}`);
      return;
    }

    if (!form.reason) {
      toast.error("Please provide a return reason");
      return;
    }

    const payload = {
      returnType: 'SALE',
      returnNo: form.billNo,
      invoiceNo: form.invoiceNo,
      items: selectedRows.map(r => ({
        inventoryId: r.inventoryId,
        itemName: r.name,
        quantity: r.returnedQty,
        reason: form.reason,
        rate: r.rate
      })),
      notes: form.notes,
      totalAmount: totalRefund,
      transportMode: form.transportMode,
      vehicleNumber: form.vehicleNo,
      trackingNumber: form.trackingNo
    };

    try {
      setLoading(true);
      await inventoryService.createMaterialReturn(payload); // Actually it's MaterialReturn
      toast.success("Return entry created successfully");
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to submit return");
    } finally {
      setLoading(false);
    }
  };

  return {
    form, setForm,
    rows, updateRow,
    loading, fetching,
    searchText, setSearchText,
    showDropdown, setShowDropdown,
    filteredInvoices,
    handleFetchInvoice,
    handleLoadSale,
    totalRefund,
    handleSubmit,
    toggleRowSelection
  };
};
